/**
 * ImageRenderer - Renders image elements
 * Supports base64 data URLs and external URLs with loading states and error handling
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement, ImageData } from '../editing/types';

export class ImageRenderer extends BaseRenderer {
  private imageNode: Konva.Image | null = null;
  private placeholderRect: Konva.Rect | null = null;
  private errorText: Konva.Text | null = null;
  private imageObj: HTMLImageElement | null = null;
  private isLoading: boolean = false;

  constructor(element: EditableElement) {
    super(element);
  }

  /**
   * Load image from src (data URL or URL)
   */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // Handle CORS for external images
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        resolve(img);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = src;
    });
  }

  /**
   * Render the image element
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    const imageData = data as ImageData | undefined;
    const src = imageData?.src;

    if (!src) {
      // Show placeholder if no src
      this.renderPlaceholder(width, height, 'No image source');
      return this._node;
    }

    // Show loading placeholder
    this.isLoading = true;
    this.renderPlaceholder(width, height, 'Loading...');

    // Load image asynchronously
    this.loadImage(src)
      .then((img) => {
        this.isLoading = false;
        this.imageObj = img;
        
        // Store natural dimensions if not already stored
        if (!imageData?.naturalWidth || !imageData?.naturalHeight) {
          const naturalWidth = img.naturalWidth || img.width;
          const naturalHeight = img.naturalHeight || img.height;
          
          // Update element data with natural dimensions
          if (this.element.data) {
            (this.element.data as ImageData).naturalWidth = naturalWidth;
            (this.element.data as ImageData).naturalHeight = naturalHeight;
          }
        }

        // Calculate dimensions if not provided
        let finalWidth = width;
        let finalHeight = height;

        if (width === 0 || height === 0) {
          // Use natural dimensions if element has no size
          let naturalWidth = img.naturalWidth || img.width || 300;
          let naturalHeight = img.naturalHeight || img.height || 200;
          
          // Apply max size limit to prevent images from being too large initially
          // Max width/height of 1200px, maintaining aspect ratio
          const maxDimension = 1200;
          if (naturalWidth > maxDimension || naturalHeight > maxDimension) {
            const scale = Math.min(maxDimension / naturalWidth, maxDimension / naturalHeight);
            naturalWidth = naturalWidth * scale;
            naturalHeight = naturalHeight * scale;
          }
          
          finalWidth = naturalWidth;
          finalHeight = naturalHeight;
          
          // Update element dimensions
          this.element.width = finalWidth;
          this.element.height = finalHeight;
          this._node.setAttr('elementWidth', finalWidth);
          this._node.setAttr('elementHeight', finalHeight);
        } else if (imageData?.preserveAspectRatio !== false) {
          // Preserve aspect ratio by default
          const aspectRatio = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
          if (width > 0 && height > 0) {
            // Fit to provided dimensions while maintaining aspect ratio
            const widthRatio = width / (img.naturalWidth || img.width);
            const heightRatio = height / (img.naturalHeight || img.height);
            const ratio = Math.min(widthRatio, heightRatio);
            
            finalWidth = (img.naturalWidth || img.width) * ratio;
            finalHeight = (img.naturalHeight || img.height) * ratio;
          }
        }

        // Remove placeholder
        if (this.placeholderRect) {
          this.placeholderRect.destroy();
          this.placeholderRect = null;
        }
        if (this.errorText) {
          this.errorText.destroy();
          this.errorText = null;
        }

        // Create image node
        this.imageNode = new Konva.Image({
          image: img,
          width: finalWidth,
          height: finalHeight,
          listening: true,
        });

        this._node.add(this.imageNode);
        this._node.getLayer()?.batchDraw();
      })
      .catch((error) => {
        this.isLoading = false;
        console.error('Failed to load image:', error);
        
        // Show error placeholder
        if (this.placeholderRect) {
          this.placeholderRect.destroy();
          this.placeholderRect = null;
        }
        this.renderPlaceholder(width, height, 'Failed to load image', true);
      });

    return this._node;
  }

  /**
   * Render placeholder rectangle
   */
  private renderPlaceholder(width: number, height: number, text: string, isError: boolean = false): void {
    // Background rectangle
    this.placeholderRect = new Konva.Rect({
      width: width || 300,
      height: height || 200,
      fill: isError ? '#FFEBEE' : '#F5F5F5',
      stroke: isError ? '#F44336' : '#CCCCCC',
      strokeWidth: 2,
      dash: [5, 5],
    });

    // Error/loading text
    this.errorText = new Konva.Text({
      text: text,
      fontSize: 14,
      fontFamily: 'Inter, sans-serif',
      fill: isError ? '#F44336' : '#666666',
      align: 'center',
      verticalAlign: 'middle',
      width: width || 300,
      height: height || 200,
    });

    this._node.add(this.placeholderRect);
    this._node.add(this.errorText);
  }

  /**
   * Update the image element with new properties
   */
  update(updates: Partial<EditableElement>): void {
    const oldSrc = (this.element.data as ImageData | undefined)?.src;
    
    this.updateElementData(updates);
    
    const { width, height, data } = this.element;
    const imageData = data as ImageData | undefined;
    const newSrc = imageData?.src;

    // If src changed, reload image
    if (newSrc && newSrc !== oldSrc) {
      // Remove old image
      if (this.imageNode) {
        this.imageNode.destroy();
        this.imageNode = null;
      }
      if (this.placeholderRect) {
        this.placeholderRect.destroy();
        this.placeholderRect = null;
      }
      if (this.errorText) {
        this.errorText.destroy();
        this.errorText = null;
      }
      
      // Re-render with new src
      this.render();
      return;
    }

    // Update image dimensions if changed
    // Note: During resize via transformer, we should respect the new dimensions
    // without forcing aspect ratio, as the user is explicitly resizing
    if (this.imageNode && (updates.width !== undefined || updates.height !== undefined)) {
      // Always update to the exact dimensions provided by the transformer
      // The transformer handles aspect ratio locking if needed (via Shift key)
      this.imageNode.width(width);
      this.imageNode.height(height);
    }

    // Update placeholder if image is not loaded
    if (this.placeholderRect && (updates.width !== undefined || updates.height !== undefined)) {
      this.placeholderRect.width(width);
      this.placeholderRect.height(height);
      if (this.errorText) {
        this.errorText.width(width);
        this.errorText.height(height);
      }
    }

    this._node.getLayer()?.batchDraw();
  }

  /**
   * Destroy the renderer and cleanup
   */
  destroy(): void {
    if (this.imageNode) {
      this.imageNode.destroy();
      this.imageNode = null;
    }
    if (this.placeholderRect) {
      this.placeholderRect.destroy();
      this.placeholderRect = null;
    }
    if (this.errorText) {
      this.errorText.destroy();
      this.errorText = null;
    }
    if (this.imageObj) {
      this.imageObj = null;
    }
    super.destroy();
  }
}
